import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import veptData from '../data/veptTests.json';
import { FaMicrophone, FaStop, FaPlay, FaCheck, FaVolumeUp, FaHeadphones, FaVolumeMute, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import '../App.css';
import './TestPage.css';
import { trackFunnelEvent } from '../utils/AnalyticsService';
import { trackGA4Event } from '../utils/GA4Analytics';
import { getAuth } from 'firebase/auth';
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import Footer from '../components/Footer';

// --- Helper Functions for Scoring ---
const calculateSimilarity = (s1, s2) => {
    if (!s1 || !s2) return 0;
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    const longerLength = longer.length;
    if (longerLength === 0) return 1.0;
    return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
};

const editDistance = (s1, s2) => {
    s1 = s1.toLowerCase();
    s2 = s2.toLowerCase();
    const costs = [];
    for (let i = 0; i <= s1.length; i++) {
        let lastValue = i;
        for (let j = 0; j <= s2.length; j++) {
            if (i === 0) costs[j] = j;
            else if (j > 0) {
                let newValue = costs[j - 1];
                if (s1.charAt(i - 1) !== s2.charAt(j - 1))
                    newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
                costs[j - 1] = lastValue;
                lastValue = newValue;
            }
        }
        if (i > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
};

const VeptTestPage = () => {
    const { testId: paramTestId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const testId = (paramTestId || location.state?.testId || 1).toString();

    const targetSectionParam = searchParams.get('section') || location.state?.section;
    const isSingleSectionMode = !!targetSectionParam;
    const initialSection = targetSectionParam || 'readAloud';

    // --- State ---
    // Direct VEPT test start
    const [selectedPattern, setSelectedPattern] = useState('vept');
    const [currentSection, setCurrentSection] = useState(initialSection); // active section index/key
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

    const isWritingOnlySection = ['typing', 'sentenceCompletion', 'dictation', 'passageReconstruction', 'summaryOpinion'].includes(initialSection);
    const [showPreTestInstruction, setShowPreTestInstruction] = useState(!isSingleSectionMode || !isWritingOnlySection); // Environment check screen
    const [isInstruction, setIsInstruction] = useState(true); // Section instruction
    const [isRecording, setIsRecording] = useState(false);
    const [isListening, setIsListening] = useState(false); // True when TTS is speaking
    const [transcript, setTranscript] = useState('');
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [score, setScore] = useState(0);

    // Typing state (Part E)
    const [typingInput, setTypingInput] = useState('');
    const [typingTimeLeft, setTypingTimeLeft] = useState(60);
    const [typingResults, setTypingResults] = useState(null);

    // Sentence Completion state (Part F)
    const [completionInput, setCompletionInput] = useState('');
    const [completionTimeLeft, setCompletionTimeLeft] = useState(25);
    const [completionAnswers, setCompletionAnswers] = useState([]);

    // Dictation state (Part G)
    const [dictationInput, setDictationInput] = useState('');
    const [dictationTimeLeft, setDictationTimeLeft] = useState(25);
    const [dictationAnswers, setDictationAnswers] = useState([]);

    // Passage Reconstruction state (Part H)
    const [passageInput, setPassageInput] = useState('');
    const [passageTimeLeft, setPassageTimeLeft] = useState(30); // 30s reading, 90s writing
    const [isReadingPhase, setIsReadingPhase] = useState(true);
    const [passageAnswers, setPassageAnswers] = useState([]);

    // Summary & Opinion state (Part I)
    const [summaryOpinionInput, setSummaryOpinionInput] = useState('');
    const [summaryOpinionTimeLeft, setSummaryOpinionTimeLeft] = useState(60); // 60s reading, 9m (540s) writing
    const [isSummaryReadingPhase, setIsSummaryReadingPhase] = useState(true);
    const [summaryOpinionAnswer, setSummaryOpinionAnswer] = useState('');

    // Speaking countdown state
    const [speakingTimeLeft, setSpeakingTimeLeft] = useState(0);

    const [checkingAccess, setCheckingAccess] = useState(true);
    const [hasAccess, setHasAccess] = useState(true);

    const recognitionRef = useRef(null);
    const transcriptRef = useRef('');
    const timersRef = useRef([]);
    const testResponsesRef = useRef([]); // Track all responses for grading and display
    const isTransitioningRef = useRef(false); // Prevents double-firing of next/save actions

    // --- Microphone Hardware Pre-Check Hooks ---
    const [preCheckStep, setPreCheckStep] = useState(1); // 1: Mic Access, 2: Record 3s, 3: Playback Verification
    const [micStream, setMicStream] = useState(null);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [testAudioBlob, setTestAudioBlob] = useState(null);
    const [isRecordingTest, setIsRecordingTest] = useState(false);
    const [testRecordTime, setTestRecordTime] = useState(3);
    const [isPlayingTest, setIsPlayingTest] = useState(false);
    const [micVolume, setMicVolume] = useState(0);
    const [micError, setMicError] = useState('');
    
    const preCheckAudioContextRef = useRef(null);
    const preCheckAnalyserRef = useRef(null);
    const preCheckAnimationFrameRef = useRef(null);
    const testChunksRef = useRef([]);
    const preCheckAudioRef = useRef(null);

    const startVolumeMonitor = async (stream) => {
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const audioContext = new AudioContextClass();
            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            
            preCheckAudioContextRef.current = audioContext;
            preCheckAnalyserRef.current = analyser;
            
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            
            const updateVolume = () => {
                if (!preCheckAnalyserRef.current) return;
                preCheckAnalyserRef.current.getByteFrequencyData(dataArray);
                let sum = 0;
                for (let i = 0; i < bufferLength; i++) {
                    sum += dataArray[i];
                }
                const average = sum / bufferLength;
                setMicVolume(Math.min(100, Math.round((average / 128) * 100)));
                preCheckAnimationFrameRef.current = requestAnimationFrame(updateVolume);
            };
            
            updateVolume();
        } catch (e) {
            console.warn("Could not start audio volume visualizer:", e);
        }
    };

    const stopVolumeMonitor = () => {
        if (preCheckAnimationFrameRef.current) {
            cancelAnimationFrame(preCheckAnimationFrameRef.current);
            preCheckAnimationFrameRef.current = null;
        }
        if (preCheckAudioContextRef.current && preCheckAudioContextRef.current.state !== 'closed') {
            preCheckAudioContextRef.current.close();
        }
        preCheckAnalyserRef.current = null;
        preCheckAudioContextRef.current = null;
    };

    const requestMicPermission = async () => {
        setMicError('');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setMicStream(stream);
            startVolumeMonitor(stream);
            setPreCheckStep(2);
        } catch (e) {
            console.error("Microphone permission denied:", e);
            setMicError("Microphone access blocked. Please click the camera/microphone icon in Chrome's address bar, set it to 'Allow', and click 'Retry Permission'.");
        }
    };

    const startTestRecording = () => {
        if (!micStream) return;
        setIsRecordingTest(true);
        setTestRecordTime(3);
        testChunksRef.current = [];

        const recorder = new MediaRecorder(micStream);
        recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) {
                testChunksRef.current.push(e.data);
            }
        };

        recorder.onstop = () => {
            const mimeType = recorder.mimeType || 'audio/webm';
            const blob = new Blob(testChunksRef.current, { type: mimeType });
            setTestAudioBlob(blob);
            setIsRecordingTest(false);
            setPreCheckStep(3);
        };

        recorder.start();
        setMediaRecorder(recorder);

        let count = 3;
        const interval = setInterval(() => {
            count--;
            setTestRecordTime(count);
            if (count === 0) {
                clearInterval(interval);
                recorder.stop();
            }
        }, 1000);
    };

    const playTestRecording = () => {
        if (!testAudioBlob || !preCheckAudioRef.current) return;
        const url = URL.createObjectURL(testAudioBlob);
        preCheckAudioRef.current.src = url;
        preCheckAudioRef.current.load();
        
        const playPromise = preCheckAudioRef.current.play();
        if (playPromise !== undefined) {
            playPromise.then(() => {
                setIsPlayingTest(true);
                preCheckAudioRef.current.onended = () => {
                    setIsPlayingTest(false);
                };
            }).catch(error => {
                console.error("Safari audio playback blocked:", error);
                setIsPlayingTest(false);
            });
        }
    };

    useEffect(() => {
        return () => {
            stopVolumeMonitor();
            if (micStream) {
                micStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [micStream]);

    // Canvas visualizer refs
    const canvasRef = useRef(null);
    const audioContextRef = useRef(null);
    const streamRef = useRef(null);

    // Helper to clear all active timers
    const clearAllTimers = () => {
        timersRef.current.forEach(id => clearTimeout(id));
        timersRef.current = [];
    };

    // Stop audio streaming tracks
    const stopAudioStream = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
    };

    // Section Order for the unified VEPT mock test
    const sectionOrder = [
        'readAloud',
        'repeats',
        'sentenceBuilds',
        'conversations',
        'typing',
        'sentenceCompletion',
        'dictation',
        'passageReconstruction',
        'summaryOpinion'
    ];

    // Add page-active body styles
    useEffect(() => {
        document.body.classList.add('test-page-active');
        document.body.classList.add('notranslate');
        return () => {
            document.body.classList.remove('test-page-active');
            document.body.classList.remove('notranslate');
        };
    }, []);

    // Helper to format seconds to mins:secs
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    // Access Control check
    useEffect(() => {
        const checkAccess = async () => {
            if (testId === "1") {
                setCheckingAccess(false);
                setHasAccess(true);
                return;
            }
            const authInstance = getAuth();
            const unsubscribe = authInstance.onAuthStateChanged(async (currentUser) => {
                if (!currentUser) {
                    setHasAccess(false);
                    navigate('/login');
                    setCheckingAccess(false);
                    return;
                }
                const userRef = doc(db, 'users', currentUser.uid);
                try {
                    const userSnap = await getDoc(userRef);
                    if (userSnap.exists()) {
                        const userData = userSnap.data();
                        const rawPremium = userData.paidTests === true || userData.hasPaid === true || userData.isPremium === true || userData.hasPremiumAccess === true;
                        let isExpired = false;
                        if (userData.premiumExpiresAt) {
                            try {
                                const expiryDate = userData.premiumExpiresAt.toDate ? userData.premiumExpiresAt.toDate() : new Date(userData.premiumExpiresAt);
                                if (new Date() > expiryDate) {
                                    isExpired = true;
                                }
                            } catch (err) {
                                console.error("Error parsing expiration date:", err);
                            }
                        }
                        const isFullPremium = rawPremium && !isExpired;
                        if (isFullPremium) {
                            setHasAccess(true);
                        } else {
                            setHasAccess(false);
                            navigate('/pricing');
                        }
                    } else {
                        setHasAccess(false);
                        navigate('/pricing');
                    }
                } catch (e) {
                    console.error(e);
                    setHasAccess(false);
                }
                setCheckingAccess(false);
            });
            return () => unsubscribe();
        };
        checkAccess();
    }, [testId, navigate]);

    // GA4 Page View Tracking
    useEffect(() => {
        trackGA4Event('instruction_page_view', { test_type: 'vept', test_id: testId || 1 });
    }, [testId]);

    // Speech Recognition Setup
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            recognition.onresult = (event) => {
                let finalTrans = '';
                for (let i = 0; i < event.results.length; ++i) {
                    finalTrans += event.results[i][0].transcript + ' ';
                }
                const cleanedTrans = finalTrans.trim();
                setTranscript(cleanedTrans);
                transcriptRef.current = cleanedTrans;
            };

            recognition.onerror = (event) => {
                console.error("Speech recognition error", event.error);
                setIsRecording(false);
                setFeedbackMessage("Error: " + event.error);
            };

            recognitionRef.current = recognition;
        } else {
            setFeedbackMessage("⚠️ Your browser does not support Speech Recognition. Please use Chrome.");
        }
    }, []);


    // Load data when section changes
    useEffect(() => {
        if (!selectedPattern) return; // Wait until pattern is selected

        clearAllTimers();
        stopAudioStream();

        const currentTestData = veptData[testId] || veptData["1"] || {};
        const sectionData = currentTestData[currentSection] || [];
        setQuestions(sectionData);
        setCurrentQuestionIndex(0);
        setIsInstruction(true);

        // Reset text/timer states based on section
        if (currentSection === 'typing') {
            setTypingInput('');
            setTypingTimeLeft(60);
        } else if (currentSection === 'sentenceCompletion') {
            setCompletionInput('');
            setCompletionTimeLeft(25);
            setCompletionAnswers([]);
        } else if (currentSection === 'dictation') {
            setDictationInput('');
            setDictationTimeLeft(25);
            setDictationAnswers([]);
        } else if (currentSection === 'passageReconstruction') {
            setPassageInput('');
            setPassageTimeLeft(30);
            setIsReadingPhase(true);
            setPassageAnswers([]);
        } else if (currentSection === 'summaryOpinion') {
            setSummaryOpinionInput('');
            setSummaryOpinionTimeLeft(60);
            setIsSummaryReadingPhase(true);
            setSummaryOpinionAnswer('');
        }

    }, [currentSection, selectedPattern, testId]);

    // Section Started tracking
    useEffect(() => {
        if (!showPreTestInstruction && !isInstruction && currentSection) {
            trackFunnelEvent(`vept_${currentSection}_started`);
        }
    }, [isInstruction, currentSection, showPreTestInstruction]);

    const activeAudioRef = useRef(null);

    // Text-to-speech function
    const stopAllSpeech = () => {
        if (activeAudioRef.current) {
            activeAudioRef.current.pause();
            activeAudioRef.current.onended = null;
            activeAudioRef.current.onerror = null;
            activeAudioRef.current = null;
        }
        if (window.utterances) {
            window.utterances.forEach(utter => {
                utter.onend = null;
                utter.onerror = null;
            });
            window.utterances = [];
        }
        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }
    };

    const speakText = (text, onEnd) => {
        if (!text) { if (onEnd) onEnd(); return; }
        if (!('speechSynthesis' in window)) {
            if (onEnd) onEnd();
            return;
        }

        stopAllSpeech();
        window.utterances = [];

        const sentences = text.match(/[^.!?]+[.!?]*/g) || [text];
        let currentChunk = 0;

        const speakNextChunk = () => {
            if (currentChunk >= sentences.length) {
                if (onEnd) onEnd();
                return;
            }

            const chunkText = sentences[currentChunk].trim();
            if (!chunkText) {
                currentChunk++;
                speakNextChunk();
                return;
            }

            const utter = new SpeechSynthesisUtterance(chunkText);
            window.utterances.push(utter);
            currentChunk++;

            const voices = window.speechSynthesis.getVoices();
            let preferredVoice = 
                voices.find(v => v.name === 'Samantha') ||
                voices.find(v => v.name === 'Karen') ||
                voices.find(v => v.name === 'Daniel') ||
                voices.find(v => v.name.includes('Microsoft Zira')) || 
                voices.find(v => v.name === 'Google US English') ||
                voices.find(v => v.lang === 'en-US' && v.localService) ||
                voices.find(v => v.lang === 'en-US') ||
                (voices.length > 0 ? voices[0] : null);

            if (preferredVoice) {
                utter.voice = preferredVoice;
            }

            utter.lang = 'en-US';
            utter.rate = 0.88; // Natural, human-like cadence (slightly relaxed for clarity)
            utter.pitch = 1.0;

            utter.onend = () => {
                // Pause naturally between dialogue sentences (250ms) for human-like pacing
                setTimeout(() => {
                    speakNextChunk();
                    window.utterances = window.utterances.filter(u => u !== utter);
                }, 250);
            };

            utter.onerror = (e) => {
                console.error("SpeechSynthesis Error:", e);
                speakNextChunk();
                window.utterances = window.utterances.filter(u => u !== utter);
            };

            window.speechSynthesis.speak(utter);
        };

        if (window.speechSynthesis.getVoices().length === 0) {
            window.speechSynthesis.onvoiceschanged = () => {
                window.speechSynthesis.onvoiceschanged = null;
                speakNextChunk();
            };
            // Fallback timeout
            setTimeout(() => {
                if (currentChunk === 0) speakNextChunk();
            }, 500);
        } else {
            speakNextChunk();
        }
    };

    const playAudioOrSpeak = (audioUrl, fallbackText, onEnd) => {
        stopAllSpeech();
        if (!audioUrl) {
            speakText(fallbackText, onEnd);
            return;
        }

        const audio = new Audio(audioUrl);
        activeAudioRef.current = audio;

        audio.onended = () => {
            activeAudioRef.current = null;
            if (onEnd) onEnd();
        };

        audio.onerror = (e) => {
            console.warn(`[Audio Player] MP3 failed at ${audioUrl}:`, e);
            activeAudioRef.current = null;
            speakText(fallbackText, onEnd);
        };

        audio.play().catch(e => {
            console.warn(`[Audio Player] Autoplay prevented for ${audioUrl}:`, e);
            activeAudioRef.current = null;
            speakText(fallbackText, onEnd);
        });
    };

    // Auto-flow timers and execution logic for all 9 sections
    useEffect(() => {
        if (showPreTestInstruction || !selectedPattern) return;

        // --- 1. Instruction Phase ---
        if (isInstruction) {
            let instructionText = "Please follow the instructions.";
            let instAudioUrl = '';

            if (currentSection === 'readAloud') {
                instructionText = "Please read the sentences as they appear on the screen. Read clearly and naturally.";
                instAudioUrl = '/audio/instructions/inst_part_a_reading.mp3';
            } else if (currentSection === 'repeats') {
                instructionText = "Please repeat each sentence that you hear. You will hear the sentence only once.";
                instAudioUrl = '/audio/instructions/inst_part_b_repeat.mp3';
            } else if (currentSection === 'sentenceBuilds') {
                instructionText = "Please rearrange the three phrases to make a correct sentence. Say the sentence clearly.";
                instAudioUrl = '/audio/instructions/inst_part_d_builds.mp3';
            } else if (currentSection === 'conversations') {
                instructionText = "You will hear a short question. Please provide a simple and direct answer in one or a few words.";
                instAudioUrl = '/audio/instructions/inst_part_c_questions.mp3';
            } else if (currentSection === 'typing') {
                instructionText = "Improve your typing speed and accuracy. You will have 1 minute to type the paragraph shown. Click 'Start Section' to begin.";
            } else if (currentSection === 'sentenceCompletion') {
                instructionText = "Test your vocabulary by completing sentences. Type one word that best completes each sentence within 25 seconds.";
            } else if (currentSection === 'dictation') {
                instructionText = "You will hear a sentence. Please type it exactly as you hear it. You will have 25 seconds.";
                instAudioUrl = '/audio/instructions/inst_part_b_repeat.mp3';
            } else if (currentSection === 'passageReconstruction') {
                instructionText = "Read the passage for 30 seconds, then rewrite it in your own words within 90 seconds.";
            } else if (currentSection === 'summaryOpinion') {
                instructionText = "Read the passage for 60 seconds. Then, write a short summary and express your opinion within 9 minutes.";
            }

            setIsListening(true);
            const instTimer = setTimeout(() => {
                playAudioOrSpeak(instAudioUrl, instructionText, () => {
                    // For Typing, user must manually click Start Section
                    if (currentSection === 'typing') {
                        setIsListening(false);
                    } else {
                        const waitTimer = setTimeout(() => {
                            setIsInstruction(false);
                            setIsListening(false);
                        }, 1500);
                        timersRef.current.push(waitTimer);
                    }
                });
            }, 600);
            timersRef.current.push(instTimer);

            return () => {
                clearAllTimers();
                stopAllSpeech();
            };
        }

        // --- 2. Active Section Questions Run ---
        if (!questions.length) return;
        const q = questions[currentQuestionIndex];
        if (!q) return;

        // Reset states
        setTranscript('');
        transcriptRef.current = '';
        setFeedbackMessage('');
        setIsRecording(false);
        setIsListening(false);
        stopAudioStream();

        if (recognitionRef.current) recognitionRef.current.abort();

        // Speaking items prep/rec times
        let prepTime = 500;
        let recordingTime = 12; // in seconds (12s for Read Aloud & Repeats)
        if (currentSection === 'readAloud') recordingTime = 12;
        if (currentSection === 'repeats') recordingTime = 12;
        if (currentSection === 'sentenceBuilds') recordingTime = 15;
        if (currentSection === 'conversations') recordingTime = 6;

        const startRecordingSequence = () => {
            setIsListening(false);
            const prepTimer = setTimeout(() => {
                handleStartRecording();
                setSpeakingTimeLeft(recordingTime);
            }, prepTime);
            timersRef.current.push(prepTimer);
        };

        // Trigger TTS prompt play for listening sections (repeats, sentenceBuilds, conversations, dictation)
        let textToPlay = '';
        let questionAudioUrl = '';
        const setStr = String(testId).padStart(2, '0');
        const qIdStr = String(q.id || (currentQuestionIndex + 1)).padStart(2, '0');

        if (currentSection === 'repeats') {
            textToPlay = q.text || q.question || '';
            questionAudioUrl = `/audio/vept/set_${setStr}/vept_set${setStr}_repeats_q${qIdStr}.mp3`;
        } else if (currentSection === 'sentenceBuilds') {
            textToPlay = Array.isArray(q.parts) ? q.parts.join(' ... ') : (q.text || q.parts || '');
            questionAudioUrl = `/audio/vept/set_${setStr}/vept_set${setStr}_builds_q${qIdStr}.mp3`;
        } else if (currentSection === 'conversations') {
            let rawQ = q.question || q.text || '';
            // Strip speaker meta-labels like "Student A:", "Tour Guide B:" so TTS reads natural dialogue
            textToPlay = rawQ.replace(/\b[A-Za-z0-9\s]{1,20}\s+[AB]:\s*/g, '');
            questionAudioUrl = `/audio/vept/set_${setStr}/vept_set${setStr}_conv_q${qIdStr}.mp3`;
        } else if (currentSection === 'dictation') {
            textToPlay = q.text || '';
            questionAudioUrl = `/audio/vept/set_${setStr}/vept_set${setStr}_dictation_q${qIdStr}.mp3`;
        }

        if (textToPlay || questionAudioUrl) {
            setIsListening(true);
            const flowTimer = setTimeout(() => {
                playAudioOrSpeak(questionAudioUrl, textToPlay, () => {
                    setIsListening(false);
                    if (currentSection === 'dictation') {
                        // Start typing countdown for dictation
                        setDictationTimeLeft(25);
                    } else {
                        startRecordingSequence();
                    }
                });
            }, 600);
            timersRef.current.push(flowTimer);
        } else {
            // Read Aloud starts recording immediately
            if (['readAloud'].includes(currentSection)) {
                startRecordingSequence();
            }
        }

        return () => {
            clearAllTimers();
            stopAllSpeech();
            stopAudioStream();
        };

    }, [currentQuestionIndex, currentSection, questions, isInstruction, showPreTestInstruction, selectedPattern]);

    // Modular Timer countdowns for each section
    // Part A-D Speaking countdown
    useEffect(() => {
        if (showPreTestInstruction || isInstruction || !selectedPattern) return;
        if (!['readAloud', 'repeats', 'sentenceBuilds', 'conversations'].includes(currentSection) || !isRecording) return;

        if (speakingTimeLeft > 0) {
            const timer = setTimeout(() => {
                setSpeakingTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (speakingTimeLeft === 0) {
            handleStopRecording();
            handleNext();
        }
    }, [speakingTimeLeft, isRecording, currentSection, isInstruction, showPreTestInstruction, selectedPattern]);

    // Part E: Typing Timer
    useEffect(() => {
        if (showPreTestInstruction || isInstruction || !selectedPattern) return;
        if (currentSection !== 'typing') return;

        if (typingTimeLeft > 0) {
            const timer = setTimeout(() => {
                setTypingTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (typingTimeLeft === 0) {
            evaluateTyping();
        }
    }, [typingTimeLeft, currentSection, isInstruction, showPreTestInstruction, selectedPattern]);

    // Part F: Sentence Completion Timer
    useEffect(() => {
        if (showPreTestInstruction || isInstruction || !selectedPattern) return;
        if (currentSection !== 'sentenceCompletion') return;

        if (completionTimeLeft > 0) {
            const timer = setTimeout(() => {
                setCompletionTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (completionTimeLeft === 0) {
            saveSentenceCompletionAnswer();
        }
    }, [completionTimeLeft, currentSection, isInstruction, showPreTestInstruction, selectedPattern]);

    // Part G: Dictation Timer (Starts after TTS prompt finishes speaking)
    useEffect(() => {
        if (showPreTestInstruction || isInstruction || !selectedPattern) return;
        if (currentSection !== 'dictation' || isListening) return;

        if (dictationTimeLeft > 0) {
            const timer = setTimeout(() => {
                setDictationTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (dictationTimeLeft === 0) {
            saveDictationAnswer();
        }
    }, [dictationTimeLeft, isListening, currentSection, isInstruction, showPreTestInstruction, selectedPattern]);

    // Part H: Passage Reconstruction Timer
    useEffect(() => {
        if (showPreTestInstruction || isInstruction || !selectedPattern) return;
        if (currentSection !== 'passageReconstruction') return;

        if (isReadingPhase && passageTimeLeft > 0) {
            const timer = setTimeout(() => {
                setPassageTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (isReadingPhase && passageTimeLeft === 0) {
            setIsReadingPhase(false);
            setPassageTimeLeft(90);
        } else if (!isReadingPhase && passageTimeLeft > 0) {
            const timer = setTimeout(() => {
                setPassageTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (!isReadingPhase && passageTimeLeft === 0) {
            savePassageReconstructionAnswer();
        }
    }, [passageTimeLeft, isReadingPhase, currentSection, isInstruction, showPreTestInstruction, selectedPattern]);

    // Part I: Summary & Opinion Timer
    useEffect(() => {
        if (showPreTestInstruction || isInstruction || !selectedPattern) return;
        if (currentSection !== 'summaryOpinion') return;

        if (isSummaryReadingPhase && summaryOpinionTimeLeft > 0) {
            const timer = setTimeout(() => {
                setSummaryOpinionTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (isSummaryReadingPhase && summaryOpinionTimeLeft === 0) {
            setIsSummaryReadingPhase(false);
            setSummaryOpinionTimeLeft(540);
        } else if (!isSummaryReadingPhase && summaryOpinionTimeLeft > 0) {
            const timer = setTimeout(() => {
                setSummaryOpinionTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (!isSummaryReadingPhase && summaryOpinionTimeLeft === 0) {
            submitVepTest();
        }
    }, [summaryOpinionTimeLeft, isSummaryReadingPhase, currentSection, isInstruction, showPreTestInstruction, selectedPattern]);

    // Global Keyboard Listener for Enter = Next / Submit
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (e.key === 'Enter') {
                if (showPreTestInstruction) return;

                // Handle instruction screen: Enter skips instruction audio and starts section
                if (isInstruction) {
                    stopAllSpeech();
                    clearAllTimers();
                    setIsListening(false);
                    setIsInstruction(false);
                    return;
                }

                // EXCLUDE multiline textareas (typing, passageReconstruction, summaryOpinion)
                if (['typing', 'passageReconstruction', 'summaryOpinion'].includes(currentSection)) {
                    return; // Let textarea insert a newline naturally
                }

                // For single-line or speaking sections: trigger Next / Submit
                e.preventDefault();
                if (['readAloud', 'repeats', 'sentenceBuilds', 'conversations'].includes(currentSection)) {
                    handleNextSpeaking();
                } else if (currentSection === 'sentenceCompletion') {
                    saveSentenceCompletionAnswer();
                } else if (currentSection === 'dictation') {
                    saveDictationAnswer();
                }
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [currentSection, isInstruction, showPreTestInstruction, completionInput, dictationInput]);

    // Audio Visualizer effect
    const startVisualizer = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = audioCtx;

            const analyser = audioCtx.createAnalyser();
            const source = audioCtx.createMediaStreamSource(stream);
            source.connect(analyser);

            analyser.fftSize = 256;
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);

            const canvas = canvasRef.current;
            if (!canvas) return;
            const canvasCtx = canvas.getContext("2d");

            const draw = () => {
                if (!isRecording) return;
                requestAnimationFrame(draw);

                analyser.getByteFrequencyData(dataArray);

                canvasCtx.fillStyle = 'rgb(15, 23, 42)'; // Dark slate match
                canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

                const barWidth = (canvas.width / bufferLength) * 2.5;
                let barHeight;
                let x = 0;

                for (let i = 0; i < bufferLength; i++) {
                    barHeight = dataArray[i] / 2.2;
                    const g = 200 * (i / bufferLength);
                    canvasCtx.fillStyle = `rgb(32, 233, 185)`; // Neon teal waves
                    canvasCtx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
                    x += barWidth + 1;
                }
            };
            draw();
        } catch (err) {
            console.error("Error accessing microphone for visualizer:", err);
        }
    };

    useEffect(() => {
        if (!isRecording) {
            stopAudioStream();
        } else {
            startVisualizer();
        }
        return () => stopAudioStream();
    }, [isRecording]);

    // Recording operations
    const handleStartRecording = () => {
        if (!recognitionRef.current) return;
        setTranscript('');
        transcriptRef.current = '';
        setFeedbackMessage('');
        setIsRecording(false);
        setIsListening(false);
        try {
            setIsRecording(true);
            recognitionRef.current.start();
        } catch (e) {
            console.error(e);
        }
    };

    const handleStopRecording = () => {
        if (!recognitionRef.current) return;
        setIsRecording(false);
        try { recognitionRef.current.stop(); } catch (e) { }

        const textToSave = transcriptRef.current || '';
        const currentQ = questions[currentQuestionIndex];

        if (currentQ) {
            const existingIdx = testResponsesRef.current.findIndex(r => r.section === currentSection && r.questionId === currentQ.id);
            if (existingIdx !== -1) return;

            // Score immediately
            const points = calculateScoreForQuestion();
            setScore(prev => prev + points);

            testResponsesRef.current.push({
                testId: testId,
                section: currentSection,
                questionId: currentQ.id,
                questionText: currentQ.text || currentQ.question || (currentQ.parts ? currentQ.parts.join('. ') : ''),
                transcript: textToSave,
                score: points,
                correctAnswer: currentQ.text || (Array.isArray(currentQ.answer) ? currentQ.answer.join(' / ') : currentQ.answer) || (currentQ.parts ? currentQ.parts.join(' ') : '')
            });
        }
    };

    const calculateScoreForQuestion = () => {
        const textToScore = transcriptRef.current;
        if (!textToScore) return 0;
        const q = questions[currentQuestionIndex];
        if (!q) return 0;

        const userText = String(textToScore).trim().toLowerCase();
        let points = 0;

        if (['readAloud', 'repeats', 'sentenceBuilds'].includes(currentSection)) {
            const target = String(q.text || q.answer || '').toLowerCase();
            const sim = calculateSimilarity(userText, target);
            if (sim > 0.85) points = 10;
            else if (sim > 0.6) points = 7;
            else if (sim > 0.4) points = 4;
        } else if (currentSection === 'conversations') {
            const raw = q.acceptableAnswers || q.answer || [];
            const rawArray = Array.isArray(raw) ? raw.flat(Infinity) : [raw];
            const answers = rawArray
                .filter(Boolean)
                .map(a => String(a).toLowerCase().trim());

            const cleanUser = userText.toLowerCase().replace(/[.,!?;:'"()–-]/g, "").replace(/\b(a|an|the)\b/g, "").replace(/\s+/g, " ").trim();

            let matched = false;
            for (const ans of answers) {
                const cleanAns = ans.toLowerCase().replace(/[.,!?;:'"()–-]/g, "").replace(/\b(a|an|the)\b/g, "").replace(/\s+/g, " ").trim();

                // 1. Direct match or clean match
                if (userText === ans || cleanUser === cleanAns) {
                    matched = true;
                    break;
                }

                // 2. Substring matching (either way)
                if (cleanUser && cleanAns && (cleanUser.includes(cleanAns) || cleanAns.includes(cleanUser))) {
                    matched = true;
                    break;
                }

                // 3. Word token overlap matching (e.g. "second friday" vs "second friday of december")
                const userWords = cleanUser.split(' ').filter(w => w.length > 2);
                const ansWords = cleanAns.split(' ').filter(w => w.length > 2);
                if (userWords.length > 0 && ansWords.length > 0) {
                    const commonWords = userWords.filter(w => ansWords.includes(w));
                    if (commonWords.length >= Math.min(userWords.length, ansWords.length)) {
                        matched = true;
                        break;
                    }
                }
            }
            points = matched ? 10 : 0;
        }
        return points;
    };

    // Progression index and section transition controller
    const handleNext = () => {
        stopAllSpeech();
        clearAllTimers();

        setCurrentQuestionIndex(prevIndex => {
            if (prevIndex < questions.length - 1) {
                return prevIndex + 1;
            } else {
                // Last question of current section -> Move to next section
                const currentIdx = sectionOrder.indexOf(currentSection);
                if (!isSingleSectionMode && currentIdx < sectionOrder.length - 1) {
                    const nextSection = sectionOrder[currentIdx + 1];
                    setTimeout(() => {
                        setIsInstruction(true);
                        setCurrentSection(nextSection);
                    }, 0);
                } else {
                    submitVepTest();
                }
                return 0; // Reset index to 0 for upcoming section
            }
        });
    };

    // Section evaluators and completion answer savers
    const evaluateTyping = () => {
        const cleanUser = typingInput.trim();
        const cleanRef = questions[0]?.paragraph || questions[0]?.text || '';
        if (!cleanUser) {
            setTypingResults({ wpm: 0, accuracy: 0, rightWords: 0, wrongWords: 0, keystrokes: 0, typingInput: '', paragraph: cleanRef });
            handleNext();
            return;
        }

        const refWords = cleanRef.split(/\s+/).filter(w => w.length > 0);
        const userWords = cleanUser.split(/\s+/).filter(w => w.length > 0);

        const m = refWords.length;
        const n = userWords.length;
        const dp = Array(m + 1).fill().map(() => Array(n + 1).fill(0));

        for (let i = 1; i <= m; i++) {
            for (let j = 1; j <= n; j++) {
                const wordA = refWords[i - 1].toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
                const wordB = userWords[j - 1].toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
                if (wordA === wordB) {
                    dp[i][j] = dp[i - 1][j - 1] + 1;
                } else {
                    dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
                }
            }
        }

        const rightWords = dp[m][n];
        const wrongWords = Math.max(0, userWords.length - rightWords);
        const keystrokes = typingInput.length;
        const timeInSeconds = Math.max(60 - typingTimeLeft, 30);
        const timeInMinutes = timeInSeconds / 60;

        const grossWpm = timeInMinutes > 0 ? Math.round((keystrokes / 5) / timeInMinutes) : 0;
        const wpm = Math.max(0, Math.round(grossWpm - (wrongWords / timeInMinutes)));
        const accuracy = userWords.length > 0 ? Math.min(100, Math.round((rightWords / userWords.length) * 100)) : 0;

        setTypingResults({
            wpm,
            accuracy,
            rightWords,
            wrongWords,
            keystrokes,
            typingInput: cleanUser,
            paragraph: cleanRef
        });
        handleNext();
    };

    const saveSentenceCompletionAnswer = () => {
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ) {
            handleNext();
            return;
        }

        const cleanAns = completionInput.trim();
        const answers = (currentQ.acceptableAnswers || [currentQ.answer] || []).map(a => a ? a.toLowerCase().trim() : '');
        const isCorrect = answers.includes(cleanAns.toLowerCase());
        const expectedAns = currentQ.answer || (currentQ.acceptableAnswers ? currentQ.acceptableAnswers.join(' / ') : '');

        setCompletionAnswers(prev => [
            ...prev,
            {
                questionId: currentQ.id || currentQuestionIndex + 1,
                questionText: currentQ.question || '',
                userAnswer: cleanAns,
                correctAnswer: expectedAns,
                isCorrect,
                score: isCorrect ? 10 : 0
            }
        ]);

        setCompletionInput('');
        setCompletionTimeLeft(25);
        handleNext();
    };

    const saveDictationAnswer = () => {
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ) {
            handleNext();
            return;
        }

        const cleanAns = dictationInput.trim();
        const sim = calculateSimilarity(cleanAns, currentQ.text || '');

        setDictationAnswers(prev => [
            ...prev,
            {
                questionId: currentQ.id || currentQuestionIndex + 1,
                questionText: currentQ.text || '',
                userAnswer: cleanAns,
                correctAnswer: currentQ.text || '',
                similarity: sim,
                score: sim > 0.85 ? 10 : sim > 0.6 ? 7 : sim > 0.4 ? 4 : 0
            }
        ]);

        setDictationInput('');
        setDictationTimeLeft(25);
        handleNext();
    };

    const savePassageReconstructionAnswer = () => {
        const currentQ = questions[currentQuestionIndex];
        if (!currentQ) {
            handleNext();
            return;
        }

        const cleanAns = passageInput.trim();
        const passageText = currentQ.passage || currentQ.paragraph || currentQ.text || currentQ.sentences || '';

        setPassageAnswers(prev => [
            ...prev,
            {
                passageId: currentQ.id || currentQuestionIndex + 1,
                originalPassage: passageText,
                userAnswer: cleanAns,
                correctAnswer: passageText
            }
        ]);

        setPassageInput('');
        setPassageTimeLeft(30);
        setIsReadingPhase(true);
        handleNext();
    };

    const handleNextSpeaking = () => {
        stopAllSpeech();
        clearAllTimers();
        handleStopRecording();
        setIsListening(false);
        setIsRecording(false);
        setSpeakingTimeLeft(0);
        handleNext();
    };

    // Submits the full VEPT payload for AI Grading
    const submitVepTest = async () => {
        stopAllSpeech();
        trackGA4Event('test_completed', { test_type: 'vept', test_id: testId || 1 });
        trackFunnelEvent(`vept_completed`);

        // If single section practice mode, populate unattempted questions with score 0 so total is calculated out of ALL section questions
        if (isSingleSectionMode && questions && questions.length > 0) {
            questions.forEach(q => {
                const exists = testResponsesRef.current.some(r => r.section === currentSection && r.questionId === q.id);
                if (!exists) {
                    testResponsesRef.current.push({
                        testId: testId,
                        section: currentSection,
                        questionId: q.id,
                        questionText: q.text || q.question || (q.parts ? q.parts.join('. ') : ''),
                        transcript: 'No Speech Recorded',
                        score: 0,
                        correctAnswer: q.text || (Array.isArray(q.answer) ? q.answer.join(' / ') : q.answer) || (q.parts ? q.parts.join(' ') : '')
                    });
                }
            });
        }

        // Finalize Summary Opinion
        const finalSummaryOpinion = {
            passage: questions[0]?.passage || '',
            prompt: questions[0]?.prompt || '',
            userAnswer: summaryOpinionAnswer.trim()
        };

        // Show loading state and process results
        navigate('/result', {
            state: {
                testId,
                mode: 'vept', // triggers unified VEPT parsing on ResultPage
                section: targetSectionParam || null,
                isSingleSection: isSingleSectionMode,
                typingResults,
                sentenceCompletionAnswers: completionAnswers,
                dictationAnswers,
                passageReconstructionAnswers: passageAnswers,
                summaryOpinionAnswer: finalSummaryOpinion,
                speakingResponses: testResponsesRef.current // Part A, B, C, D
            }
        });
    };

    // Card Selection Action Handler
    const handleStartTest = () => {
        trackGA4Event('test_started', { test_type: 'vept', test_id: testId || 1 });
        trackFunnelEvent('test_started', { test_type: 'vept', test_id: testId || 1 });
        stopAllSpeech();
        setShowPreTestInstruction(false);
    };

    // Render loading or error states
    if (checkingAccess) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center font-sans text-white">
                <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-slate-400 font-light">Verifying credentials and mock settings...</p>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center font-sans text-white">
                <div className="w-20 h-20 bg-red-500/10 text-red-500 border border-red-500/20 rounded-3xl flex items-center justify-center text-4xl mb-6">🔒</div>
                <h3 className="text-3xl font-extrabold mb-3">Premium Access Required</h3>
                <p className="text-slate-400 max-w-md mb-8">This mock test belongs to our exclusive premium library. Please purchase our test prep package to unlock it.</p>
                <button onClick={() => navigate('/pricing')} className="px-8 py-3.5 bg-emerald-500 hover:bg-emerald-600 rounded-full font-bold text-slate-950 transition-all shadow-[0_5px_15px_rgba(16,185,129,0.3)]">View Plans</button>
            </div>
        );
    }

    // --- Pre-Test Environment Check Screen ---
    if (showPreTestInstruction) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 sm:p-6 font-sans">
                <audio ref={preCheckAudioRef} className="hidden" playsInline />
                <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-4xl overflow-hidden animate-fade-in flex flex-col md:flex-row">

                    {/* Header Side (Left on desktop, Top on mobile) */}
                    <div className="bg-gradient-to-br from-indigo-900 to-indigo-700 p-8 md:p-12 text-white text-center flex flex-col justify-center relative md:w-2/5">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-md shadow-inner border border-white/20">
                            <FaHeadphones className="text-3xl sm:text-4xl text-indigo-100 animate-bounce" />
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-black mb-3 tracking-tight">Audio Check</h2>
                        <p className="text-indigo-200 text-sm sm:text-base font-medium leading-relaxed">
                            Verify your microphone and hardware setup to ensure accurate scoring for VEPT Test {testId}.
                        </p>
                        
                        {/* Progress Steps Indicators */}
                        <div className="mt-8 flex items-center justify-center gap-3 relative z-10">
                            <span className={`w-3 h-3 rounded-full transition-all duration-300 ${preCheckStep === 1 ? 'bg-white scale-125 shadow-md' : 'bg-white/30'}`}></span>
                            <span className={`w-3 h-3 rounded-full transition-all duration-300 ${preCheckStep === 2 ? 'bg-white scale-125 shadow-md' : 'bg-white/30'}`}></span>
                            <span className={`w-3 h-3 rounded-full transition-all duration-300 ${preCheckStep === 3 ? 'bg-white scale-125 shadow-md' : 'bg-white/30'}`}></span>
                        </div>
                    </div>

                    {/* Body Side (Right on desktop, Bottom on mobile) */}
                    <div className="p-6 sm:p-8 md:p-10 flex flex-col justify-between md:w-3/5 min-h-[450px]">
                        
                        {/* Step 1: Request Mic Access */}
                        {preCheckStep === 1 && (
                            <div className="flex-1 flex flex-col justify-center">
                                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-black flex items-center justify-center">1</span>
                                    Microphone Access
                                </h3>
                                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                                    This placement test evaluates your spoken English. We need permission to access your microphone. Click the button below to request access.
                                </p>
                                
                                {micError ? (
                                    <div className="bg-red-50 border border-red-100 rounded-2xl p-4 text-sm text-red-700 mb-6 leading-relaxed animate-shake flex gap-3 items-start">
                                        <span className="text-lg mt-0.5">⚠️</span>
                                        <div>
                                            <strong className="block font-bold mb-1">Access Blocked</strong>
                                            {micError}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-indigo-50/50 border border-indigo-100 p-5 rounded-2xl flex items-center gap-4 mb-6">
                                        <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0 shadow-sm animate-pulse">
                                            <FaMicrophone />
                                        </div>
                                        <p className="text-xs text-indigo-900 font-semibold leading-relaxed">
                                            Chrome will ask for permission when you click. Tap "Allow" to proceed.
                                        </p>
                                    </div>
                                )}

                                <button
                                    onClick={requestMicPermission}
                                    className="w-full py-4 rounded-2xl font-black text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 hover:-translate-y-0.5 transition-all text-sm uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer"
                                >
                                    Enable Microphone
                                </button>
                            </div>
                        )}

                        {/* Step 2: Record Audio Test */}
                        {preCheckStep === 2 && (
                            <div className="flex-1 flex flex-col justify-center">
                                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-black flex items-center justify-center">2</span>
                                    Voice Calibration
                                </h3>
                                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                                    Speak into your microphone. Say: <strong className="text-indigo-600">"Testing microphone"</strong>. We will record a short 3-second sample to verify it works.
                                </p>

                                {/* Volume Bar Indicator */}
                                <div className="mb-6 bg-gray-50 border border-gray-100 p-5 rounded-2xl">
                                    <span className="text-xs uppercase tracking-wider font-extrabold text-gray-400 block mb-2">Live Microphone Signal</span>
                                    <div className="w-full bg-gray-200 h-3 rounded-full overflow-hidden relative shadow-inner">
                                        <div className="h-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-indigo-700 transition-all duration-75" style={{ width: `${micVolume}%` }}></div>
                                    </div>
                                    <span className="text-[10px] text-gray-400 font-bold block mt-1.5">{micVolume > 10 ? '✨ Signal detected. Speak now.' : '🎤 Start speaking to test level.'}</span>
                                </div>

                                {isRecordingTest ? (
                                    <div className="w-full py-5 rounded-2xl border-2 border-indigo-600 bg-indigo-50/50 flex flex-col items-center justify-center text-center animate-pulse">
                                        <div className="w-4 h-4 rounded-full bg-red-600 animate-ping mb-3"></div>
                                        <span className="font-extrabold text-indigo-900 text-lg mb-1">RECORDING VOICE CHECK</span>
                                        <span className="text-sm text-indigo-600 font-medium">Stopping automatically in {testRecordTime} seconds...</span>
                                    </div>
                                ) : (
                                    <button
                                        onClick={startTestRecording}
                                        className="w-full py-4 rounded-2xl font-black text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 hover:-translate-y-0.5 transition-all text-sm uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer"
                                    >
                                        Start 3s Voice Check
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Step 3: Playback Confirm */}
                        {preCheckStep === 3 && (
                            <div className="flex-1 flex flex-col justify-center">
                                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-black flex items-center justify-center">3</span>
                                    Audio Verification
                                </h3>
                                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                                    Listen to your voice recording to make sure your headset is selected and sounds clean.
                                </p>

                                <button
                                    onClick={playTestRecording}
                                    className={`w-full py-4 rounded-2xl font-black border-2 transition-all flex items-center justify-center gap-3 mb-6 cursor-pointer ${isPlayingTest ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50' : 'border-gray-200 text-gray-700 bg-white hover:bg-gray-50'}`}
                                >
                                    {isPlayingTest ? (
                                        <>
                                            <div className="w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                            Playing Back Your Voice...
                                        </>
                                    ) : (
                                        <>
                                            <FaPlay className="text-xs" />
                                            Play Back Sample Recording
                                        </>
                                    )}
                                </button>

                                <div className="border-t border-gray-100 pt-6">
                                    <p className="text-sm text-gray-700 font-bold mb-4 text-center">Can you hear your voice sample clearly?</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => {
                                                setPreCheckStep(2);
                                                setTestAudioBlob(null);
                                            }}
                                            className="py-3 rounded-xl border border-gray-200 hover:bg-gray-50 font-bold text-gray-600 text-xs transition-colors cursor-pointer"
                                        >
                                            No, Record Again 🔄
                                        </button>
                                        <button
                                            onClick={() => {
                                                handleStartTest();
                                                stopVolumeMonitor();
                                                if (micStream) {
                                                    micStream.getTracks().forEach(track => track.stop());
                                                }
                                            }}
                                            className="py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 font-black text-white text-xs shadow-md shadow-indigo-600/10 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                                        >
                                            Yes, Begin Test <FaPlay className="text-[8px]" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* PreCheck Cancel Footer */}
                        {preCheckStep < 3 && (
                            <div className="flex justify-end border-t border-gray-100 pt-6 mt-4">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="px-6 py-2.5 rounded-xl font-bold text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-all text-xs cursor-pointer"
                                >
                                    Cancel & Exit
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // --- 3. Section Instruction Screen (Standardized styling) ---
    if (isInstruction) {
        return (
            <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-6 text-center font-sans text-white relative">
                <div className="w-full max-w-xl p-8 bg-slate-800 rounded-3xl border border-slate-700 shadow-2xl animate-fade-in">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full mb-4 inline-block">
                        Part {sectionOrder.indexOf(currentSection) + 1} of 9
                    </span>
                    <h2 className="text-3xl font-black mb-4 capitalize">
                        {currentSection.replace(/([A-Z])/g, ' $1').trim()} Instructions
                    </h2>
                    <p className="text-slate-300 leading-relaxed mb-8">
                        {currentSection === 'readAloud' && "Read each sentence aloud as it appears on the screen. Speak clearly and project your voice."}
                        {currentSection === 'repeats' && "Listen to each sentence carefully and repeat it exactly as you hear it."}
                        {currentSection === 'sentenceBuilds' && "Rearrange the given parts to make a meaningful, correct sentence."}
                        {currentSection === 'conversations' && "Listen to a simple question and give a direct, one-word or short answer."}
                        {currentSection === 'typing' && "Type the paragraph exactly as it appears. You have 1 minute. The timer starts when you click below."}
                        {currentSection === 'sentenceCompletion' && "Type one word to complete the sentence. You have 25 seconds for each question."}
                        {currentSection === 'dictation' && "Listen to a sentence and type it exactly as you heard it. You have 25 seconds."}
                        {currentSection === 'passageReconstruction' && "You will have 30 seconds to read a passage, then 90 seconds to rewrite it from memory."}
                        {currentSection === 'summaryOpinion' && "Read a passage for 1 minute. Then summarize the passage and express your opinion in at least 100 words within 9 minutes."}
                    </p>

                    {isListening && (
                        <div className="flex items-center justify-center gap-2 mb-6 text-emerald-400 animate-pulse text-sm">
                            <FaVolumeUp className="text-xl" />
                            <span>Listening to instructions...</span>
                        </div>
                    )}

                    <button
                        onClick={() => {
                            stopAllSpeech();
                            clearAllTimers();
                            setIsListening(false);
                            setIsInstruction(false);
                        }}
                        className="px-8 py-3.5 rounded-full font-bold text-slate-950 transition-all bg-emerald-400 hover:bg-emerald-500 shadow-[0_4px_15px_rgba(52,211,153,0.3)] cursor-pointer"
                    >
                        Start Section
                    </button>
                </div>
            </div>
        );
    }

    // --- 4. Main Section Execution Screen ---
    const activeQuestion = questions[currentQuestionIndex];

    const getSectionDisplayName = () => {
        switch (currentSection) {
            case 'readAloud': return 'Part A: Read Aloud';
            case 'repeats': return 'Part B: Repeats';
            case 'sentenceBuilds': return 'Part C: Sentence Builds';
            case 'conversations': return 'Part D: Conversations';
            case 'typing': return 'Part E: Typing Test';
            case 'sentenceCompletion': return 'Part F: Sentence Completion';
            case 'dictation': return 'Part G: Dictation';
            case 'passageReconstruction': return 'Part H: Passage Reconstruction';
            case 'summaryOpinion': return 'Part I: Summary & Opinion';
            default: return 'VEPT Mock Test';
        }
    };

    return (
        <>
            <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 font-sans">
                <div className="test-container-glass">
                    {/* Exit corner button */}
                    <button onClick={() => navigate('/dashboard')} className="test-btn-exit-corner">
                        &times;
                    </button>

                    <h2 className="test-title">{getSectionDisplayName()}</h2>
                    <p className="test-subtitle">
                        Question {Math.min(currentQuestionIndex + 1, questions.length || 1)} of {questions.length || 1} (Part {sectionOrder.indexOf(currentSection) + 1} of 9)
                    </p>

                    {!activeQuestion && !isInstruction && (
                        <div className="text-center w-full py-12 animate-pulse space-y-4">
                            <FaVolumeUp className="text-4xl text-emerald-400 mx-auto" />
                            <p className="text-emerald-300 font-bold">Loading section content...</p>
                        </div>
                    )}

                    {/* 4.A Speaking Sections Arena (Read Aloud, Repeats, Builds, Q&A) */}
                    {['readAloud', 'repeats', 'sentenceBuilds', 'conversations'].includes(currentSection) && (
                        <div className="text-center w-full space-y-6 animate-fade-in">
                            {isRecording && (
                                <div className="timer-display-circular">
                                    <div className="timer-text-circular">{speakingTimeLeft}s</div>
                                </div>
                            )}

                            {currentSection === 'readAloud' && activeQuestion && (
                                <div className="test-question-area text-xl leading-relaxed text-center font-medium">
                                    {activeQuestion.text}
                                </div>
                            )}
                            {currentSection === 'sentenceBuilds' && activeQuestion && (
                                <div className="test-question-area text-xl leading-relaxed text-center font-bold tracking-wide">
                                    {activeQuestion.jumbled}
                                </div>
                            )}
                            {currentSection === 'conversations' && activeQuestion && (
                                <div className="test-question-area text-xl leading-relaxed text-center font-medium">
                                    <FaVolumeUp className="inline-block text-emerald-400 mr-2 text-2xl animate-pulse" /> Listen to the question carefully.
                                </div>
                            )}
                            {currentSection === 'repeats' && activeQuestion && (
                                <div className="test-question-area text-xl leading-relaxed text-center font-medium">
                                    <FaVolumeUp className="inline-block text-emerald-400 mr-2 text-2xl animate-pulse" /> Listen to the sentence carefully.
                                </div>
                            )}

                            {isListening && (
                                <div className="flex flex-col items-center justify-center space-y-2 mt-4">
                                    <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center text-xl animate-bounce">
                                        <FaVolumeUp />
                                    </div>
                                    <p className="text-emerald-300 text-sm animate-pulse">Playing audio...</p>
                                </div>
                            )}

                            {isRecording && (
                                <div className="flex flex-col items-center justify-center space-y-4 mt-4">
                                    <canvas ref={canvasRef} width="400" height="80" className="w-full max-w-sm bg-black/40 rounded-2xl border border-white/10 shadow-inner" />
                                    <div className="flex items-center gap-2 text-emerald-400 text-sm animate-pulse font-bold">
                                        <FaMicrophone className="text-lg" />
                                        <span>Recording... Speak now</span>
                                    </div>
                                    {transcript && (
                                        <p className="text-gray-300 italic text-sm mt-2 max-w-md mx-auto bg-black/20 p-3 rounded-xl border border-white/5">"{transcript}"</p>
                                    )}
                                </div>
                            )}

                            {!isListening && !isRecording && (
                                <p className="text-gray-400 text-sm italic mt-4">Preparing recording...</p>
                            )}

                            <button 
                                onClick={handleNextSpeaking}
                                className="test-btn test-btn-primary w-full mt-6"
                            >
                                Next
                            </button>
                        </div>
                    )}

                    {/* 4.B Part E: Typing Arena */}
                    {currentSection === 'typing' && activeQuestion && (
                        <div className="w-full space-y-4 animate-fade-in">
                            <div className="timer-display-circular">
                                <div className="timer-text-circular">{formatTime(typingTimeLeft)}</div>
                            </div>
                            <div className="test-question-area text-lg leading-relaxed text-slate-200 select-none">
                                {activeQuestion.paragraph || activeQuestion.text}
                            </div>
                            <textarea
                                value={typingInput}
                                onChange={(e) => setTypingInput(e.target.value)}
                                className="test-textarea h-40 font-mono"
                                placeholder="Start typing here..."
                                disabled={typingTimeLeft <= 0}
                                autoFocus
                            />
                            <button
                                onClick={evaluateTyping}
                                disabled={typingTimeLeft > 0 && typingInput.trim().length === 0}
                                className={`test-btn w-full ${typingTimeLeft > 0 ? 'test-btn-secondary' : 'test-btn-primary'}`}
                            >
                                {typingTimeLeft > 0 ? 'Press Enter to Skip' : 'Continue'}
                            </button>
                        </div>
                    )}

                    {/* 4.C Part F: Sentence Completion Arena */}
                    {currentSection === 'sentenceCompletion' && activeQuestion && (
                        <div className="w-full space-y-4 animate-fade-in">
                            <div className="timer-display-circular">
                                <div className="timer-text-circular">{formatTime(completionTimeLeft)}</div>
                            </div>
                            <div className="test-question-area text-xl text-center leading-relaxed font-semibold">
                                {activeQuestion.question}
                            </div>
                            <input
                                type="text"
                                value={completionInput}
                                onChange={(e) => setCompletionInput(e.target.value.replace(/[^a-zA-Z]/g, ""))}
                                className="test-input text-center text-xl font-bold tracking-wide"
                                placeholder="Type single word blank answer..."
                                autoFocus
                            />
                            <button
                                onClick={saveSentenceCompletionAnswer}
                                disabled={!completionInput.trim()}
                                className="test-btn test-btn-primary w-full"
                            >
                                Submit Answer
                            </button>
                        </div>
                    )}

                    {/* 4.D Part G: Dictation Arena */}
                    {currentSection === 'dictation' && activeQuestion && (
                        <div className="w-full space-y-4 animate-fade-in text-center">
                            <div className="timer-display-circular">
                                <div className="timer-text-circular">{formatTime(dictationTimeLeft)}</div>
                            </div>
                            <div className="test-question-area flex items-center justify-center py-8">
                                <FaVolumeUp className="text-5xl text-emerald-400 animate-pulse" />
                            </div>
                            {!isListening && (
                                <>
                                    <input
                                        type="text"
                                        value={dictationInput}
                                        onChange={(e) => setDictationInput(e.target.value)}
                                        className="test-input text-center text-lg"
                                        placeholder="Type what you heard..."
                                        autoFocus
                                    />
                                    <button
                                        onClick={saveDictationAnswer}
                                        disabled={!dictationInput.trim()}
                                        className="test-btn test-btn-primary w-full"
                                    >
                                        Submit Answer
                                    </button>
                                </>
                            )}
                            {isListening && (
                                <p className="text-emerald-300 text-sm animate-pulse">Playing dictation audio, please listen...</p>
                            )}
                        </div>
                    )}

                    {/* 4.E Part H: Passage Reconstruction Arena */}
                    {currentSection === 'passageReconstruction' && activeQuestion && (
                        <div className="w-full space-y-4 animate-fade-in">
                            <div className="timer-display-circular">
                                <div className="timer-text-circular">{formatTime(passageTimeLeft)}</div>
                            </div>
                            {isReadingPhase ? (
                                <>
                                    <p className="text-emerald-300 text-sm font-bold animate-pulse text-center">Reading Phase — Memorize the passage below</p>
                                    <div className="test-question-area text-lg leading-relaxed text-slate-200">
                                        {activeQuestion.passage || activeQuestion.paragraph || activeQuestion.text || activeQuestion.sentences}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="text-teal-300 text-sm font-bold text-center">Reconstruct the passage in your own words</p>
                                    <textarea
                                        value={passageInput}
                                        onChange={(e) => setPassageInput(e.target.value)}
                                        className="test-textarea h-48"
                                        placeholder="Type reconstruction here..."
                                        autoFocus
                                    />
                                    <button
                                        onClick={savePassageReconstructionAnswer}
                                        disabled={!passageInput.trim()}
                                        className="test-btn test-btn-primary w-full"
                                    >
                                        Submit Reconstruction
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {/* 4.F Part I: Summary & Opinion Arena */}
                    {currentSection === 'summaryOpinion' && activeQuestion && (
                        <div className="w-full space-y-4 animate-fade-in">
                            <div className="timer-display-circular">
                                <div className="timer-text-circular">{formatTime(summaryOpinionTimeLeft)}</div>
                            </div>
                            {isSummaryReadingPhase ? (
                                <>
                                    <p className="text-emerald-300 text-sm font-bold animate-pulse text-center">Reading Phase — Read the passage carefully</p>
                                    <div className="test-question-area text-lg leading-relaxed text-slate-200">
                                        {activeQuestion.passage}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="text-teal-300 text-sm font-bold text-center">Prompt: {activeQuestion.prompt}</p>
                                    <textarea
                                        value={summaryOpinionAnswer}
                                        onChange={(e) => setSummaryOpinionAnswer(e.target.value)}
                                        className="test-textarea h-48"
                                        placeholder="Write your summary and opinion here (minimum 100 words recommended)..."
                                        autoFocus
                                    />
                                    <div className="flex justify-between items-center text-xs text-slate-500 mt-2">
                                        <span>Word Count: {summaryOpinionAnswer.trim() ? summaryOpinionAnswer.trim().split(/\s+/).length : 0} words</span>
                                        <span>Minimum target: 100 words</span>
                                    </div>
                                    <button
                                        onClick={submitVepTest}
                                        disabled={!summaryOpinionAnswer.trim()}
                                        className="test-btn test-btn-primary w-full"
                                    >
                                        Finish Mock Test & View Report
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};

export default VeptTestPage;
